import React, { useEffect, useMemo, useState } from 'react';
import apiClient, { buildApiUrl } from '../config/api';
import { useNavigate } from 'react-router-dom';

import Navbar from '../components/Navbar';
import { styles } from '../styles/newstyles'; // keep your global styles
import '../styles/OwnerDetail.css';           // keep table/modal styling

const UP_ARROW = String.fromCharCode(9650);
const DOWN_ARROW = String.fromCharCode(9660);

const OwnerDetailPage = ({ onLogout}) => {
  const [username, setUsername] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  // data
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);

  // filters / selections
  const [year, setYear] = useState(2026);
  const [countyId, setCountyId] = useState(null);
  const [counties, setCounties] = useState([]);

  // ui state
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });

  // attachments
  const [attachments, setAttachments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedOwnerNbr, setSelectedOwnerNbr] = useState(null);
  const [selectedOwnerName, setSelectedOwnerName] = useState('');
  const [activeOwnerNbr, setActiveOwnerNbr] = useState(null);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [camaSearchTerm, setCamaSearchTerm] = useState('');
  const [camaResults, setCamaResults] = useState([]);
  const [camaSearchActive, setCamaSearchActive] = useState(false);
  const [camaLoading, setCamaLoading] = useState(false);
  const [camaError, setCamaError] = useState('');
  const [lastCamaIdSearched, setLastCamaIdSearched] = useState(null);
  const [camaTotalCount, setCamaTotalCount] = useState(null);

  const navigate = useNavigate();

  // Load session; for admins also load counties
  useEffect(() => {
    const boot = async () => {
      try {
        const sess = await apiClient.get('/auth/session', { withCredentials: true });
        if (sess.data?.username) setUsername(sess.data.username);
        const admin = !!sess.data?.isAdmin;
        setIsAdmin(admin);

        if (admin) {
          // Admins must pick county first
          const res = await apiClient.get('/lookup/counties', { withCredentials: true });
          setCounties(res.data || []);
        } else {
          // Non-admins get county from session and fetch immediately
          setCountyId(sess.data?.countyID ?? null);
        }
      } catch {
        // ignore; router guard will handle redirect if needed
      }
    };
    boot();
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Fetch data when year or county changes (county must be selected/known)
  useEffect(() => {
    const fetchData = async () => {
      if (!countyId || !year) return;
      try {
        setLoading(true);
        const res = await apiClient.get('/owners/owner-details', {
          params: { appraisalYear: year, countyId },
          withCredentials: true
        });
        setData(res.data || []);
      } catch (err) {
        console.error('Failed to fetch owner details:', err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [year, countyId]);

  // Filter on search term
  useEffect(() => {
    const filteredData = data.filter((item) =>
      (item.OwnerName || '').toLowerCase().includes((debouncedSearchTerm || '').toLowerCase())
    );
    setFiltered(filteredData);
  }, [debouncedSearchTerm, data]);

  useEffect(() => {
    if (!activeOwnerNbr) {
      return;
    }

    const stillVisible = filtered.some((item) => item.OwnerNbr === activeOwnerNbr);
    if (!stillVisible) {
      setActiveOwnerNbr(null);
    }
  }, [activeOwnerNbr, filtered]);

  useEffect(() => {
    setCamaSearchActive(false);
    setCamaResults([]);
    setCamaError('');
    setLastCamaIdSearched(null);
    setCamaTotalCount(null);
  }, [countyId]);
  const sortedData = useMemo(() => {
    if (!sortConfig.column) return filtered;
    const columnKey = sortConfig.column;
    const isNumericColumn = columnKey === 'CurrentValue' || columnKey === 'PriorValue';
    const directionMultiplier = sortConfig.direction === 'asc' ? 1 : -1;

    const normalizeNumber = (value) => {
      if (value === null || value === undefined) {
        return 0;
      }
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      const sanitized = String(value).replace(/[^0-9.-]+/g, '');
      const numeric = Number(sanitized);
      return Number.isFinite(numeric) ? numeric : 0;
    };

    const normalizeString = (value) => {
      if (value === null || value === undefined) {
        return '';
      }
      return String(value).trim().toLowerCase();
    };

    return [...filtered].sort((a, b) => {
      const aRaw = a[columnKey];
      const bRaw = b[columnKey];

      if (isNumericColumn) {
        const aValue = normalizeNumber(aRaw);
        const bValue = normalizeNumber(bRaw);
        if (aValue === bValue) {
          return 0;
        }
        return aValue > bValue ? directionMultiplier : -directionMultiplier;
      }

      const aValue = normalizeString(aRaw);
      const bValue = normalizeString(bRaw);
      const comparison = aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
      if (comparison === 0) {
        return 0;
      }
      return comparison * directionMultiplier;
    });
  }, [filtered, sortConfig]);

  const countyLabel = useMemo(() => {
    if (!countyId) {
      return 'no-county';
    }
    const match = (counties || []).find((county) => String(county.CountyID) === String(countyId));
    const rawName = match?.Name || match?.CountyName;
    if (rawName) {
      return String(rawName).trim().replace(/\s+/g, '-').toLowerCase();
    }
    return `county-${countyId}`;
  }, [countyId, counties]);

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const toDisplayUpper = (value, fallback = '-') => {
    const normalized = value === null || value === undefined ? '' : String(value).trim();
    return normalized ? normalized.toUpperCase() : fallback;
  };

  const formatUploadDate = (value) => {
    if (!value) {
      return '-';
    }
    const stringValue = typeof value === 'string' ? value.trim() : value;
    const treatAsUtc = typeof stringValue === 'string' && stringValue.toUpperCase().endsWith('Z');
    const date = new Date(stringValue);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: treatAsUtc ? 'UTC' : undefined,
      year: '2-digit',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return formatter.format(date).replace(', ', ' ');
  };

  const renderSortableHeader = (columnKey, label, options = {}) => {
    const { align = 'center' } = options;
    const isActive = sortConfig.column === columnKey;
    const isAscending = isActive && sortConfig.direction === 'asc';
    const isDescending = isActive && sortConfig.direction === 'desc';
    const upArrowClass = 'sort-arrow sort-arrow--up' + (isAscending ? ' active' : '');
    const downArrowClass = 'sort-arrow sort-arrow--down' + (isDescending ? ' active' : '');
    const buttonClass = 'sort-arrow-group' + (isActive ? ' active' : '');
    const wrapperClass =
      'sortable-header' + (align === 'left' ? ' sortable-header--left' : ' sortable-header--center');

    return (
      <th className={align === 'center' ? 'numeric-header' : ''}>
        <div className={wrapperClass}>
          <span>{label}</span>
          <button
            type="button"
            className={buttonClass}
            onClick={() => handleSort(columnKey)}
            aria-label={'Sort ' + label}
          >
            <span className={upArrowClass}>{UP_ARROW}</span>
            <span className={downArrowClass}>{DOWN_ARROW}</span>
          </button>
        </div>
      </th>
    );
  };
  const handleYearChange = (e) => setYear(parseInt(e.target.value, 10));

  const handleViewAttachments = async (ownerNbr, ownerName) => {
    if (attachmentsLoading) {
      return;
    }

    try {
      setAttachmentsLoading(true);
      setSelectedOwnerNbr(ownerNbr);
      setActiveOwnerNbr(ownerNbr);
      setSelectedOwnerName(ownerName || '');

      const res = await apiClient.get('/owners/owner-attachments', {
        params: { ownerNbr, appraisalYear: year },
        withCredentials: true
      });
      setAttachments(res.data || []);
      setShowModal(true);
    } catch (err) {
      console.error('Failed to fetch attachments:', err);
      setAttachments([]);
      setShowModal(true);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!sortedData || sortedData.length === 0) {
      return;
    }

    const headers = ['Owner Name','CAD Owner ID', 'Agent Name', 'Current Value', 'Prior Value'];
    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) {
        return '""';
      }
      const stringValue = String(value).replace(/"/g, '""');
      return `"${stringValue}"`;
    };
    const normalizeCurrency = (value) => {
      if (value === null || value === undefined) {
        return '';
      }
      const numeric = Number(value);
      if (Number.isFinite(numeric)) {
        return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return String(value);
    };

    const rows = sortedData.map((row) => [
      row.OwnerName || '',
      row.CAD_OwnerID || '',
      row.AgentName || '',
      normalizeCurrency(row.CurrentValue),
      normalizeCurrency(row.PriorValue),
    ]);

    const csvContent = [
      headers.map(escapeCsvValue).join(','),
      ...rows.map((fields) => fields.map(escapeCsvValue).join(',')),
    ].join('\n');

    const filenameCountyPart = countyLabel || (countyId ? `county-${countyId}` : 'all-counties');
    const filename = `owner-details_${filenameCountyPart}_${year}.csv`;

    if (typeof window === 'undefined') {
      return;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCamaSearch = async () => {
    const trimmedId = String(camaSearchTerm || '').trim();
    if (!trimmedId) {
      setCamaError('Please enter a CAMA ID to search.');
      return;
    }
    if (!countyId) {
      setCamaError('Please select a county before searching by CAMA ID.');
      return;
    }

    setCamaLoading(true);
    setCamaError('');
    setLastCamaIdSearched(trimmedId);
    setCamaTotalCount(null);

    try {
      const res = await apiClient.get('/owners/owner-attachments/by-cama', {
        params: { CAMAID: trimmedId, GeoNbr: Number(countyId) },
        withCredentials: true
      });
      const results = res.data || [];
      const totalFromApi = Number(results?.[0]?.TotalDocuments);
      setCamaTotalCount(Number.isFinite(totalFromApi) ? totalFromApi : results.length);
      setCamaResults(results);
    } catch (err) {
      console.error('Failed to fetch attachments by CAMA ID:', err);
      setCamaResults([]);
      setCamaError('Unable to fetch attachments for that CAMA ID.');
    } finally {
      setCamaSearchActive(true);
      setCamaLoading(false);
    }
  };

  const handleClearCamaSearch = () => {
    setCamaSearchTerm('');
    setCamaResults([]);
    setCamaSearchActive(false);
    setCamaError('');
    setLastCamaIdSearched(null);
    setCamaTotalCount(null);
  };

  const handleViewCamaFile = (record) => {
    if (!record?.FileData) {
      console.warn('CAMA attachment has no file data.');
      return;
    }

    try {
      const byteCharacters = atob(record.FileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const mimeType = record.FileType || 'application/octet-stream';
      const blob = new Blob([byteArray], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      const newWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        console.error('Unable to open the attachment in a new tab.');
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.error('Failed to open file from CAMA search:', err);
      setCamaError('Unable to open the selected file.');
    }
  };

  // Simple adapters for Navbar actions (unchanged)
  const handleTabChange = (tab) => {
    if (tab === 'home') navigate('/home');
    if (tab === 'recyclebin') navigate('/recyclebin');
    if (tab === 'owner') navigate('/owner');
  };
  const handleShowForm = () => navigate('/add');

  return (
    <div style={{ ...styles.mainContainer, margin: '25px auto' }}>
      <Navbar
        username={username}
        isAdmin={isAdmin}
        setShowForm={handleShowForm}
        onTabChange={handleTabChange}
         onLogout={onLogout}
      />

      <div className="owner-container">
        <h2 style={styles.heading}>Owner Details {year ? `(${year})` : ''}</h2>

        <div className="owner-filters">
          {/* Admin: pick county first */}
          {isAdmin && (
            <div className="owner-section owner-section--county">
              <label className="owner-select-label" htmlFor="county-select">County:</label>
              <select
                id="county-select"
                className="owner-select owner-select--county"
                value={countyId || ''}
                onChange={(e) => setCountyId(e.target.value ? parseInt(e.target.value, 10) : null)}
              >
                <option value="">-- Select County --</option>
                {counties.map(c => (
                  <option key={c.CountyID} value={c.CountyID}>{c.Name || c.CountyName}</option>
                ))}
              </select>
              {!countyId && (
                <p className="owner-no-records" style={{ marginTop: 8 }}>
                  Please select a county.
                </p>
              )}
            </div>
          )}

          {/* Show Year/Search controls only when we have a county (admin) or always for non-admin */}
          {(!isAdmin || countyId) && (
            <div className="owner-controls">
              <div className="owner-control owner-control--year">
                <label className="owner-select-label" htmlFor="appraisal-year">Appraisal Year:</label>
                <select
                  id="appraisal-year"
                  value={year}
                  onChange={handleYearChange}
                  className="owner-select"
                >
                  {Array.from({ length: 9 }, (_, i) => {
                    const y = 2026 - i;
                    return <option key={y} value={y}>{y}</option>;
                  })}
                </select>
              </div>

              <div className="owner-control owner-control--search">
                <label className="owner-select-label" htmlFor="owner-search">Search By Owner:</label>
                <input
                  id="owner-search"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Enter Owner Name..."
                  className="owner-select owner-search-input"
                />
                <div className="cama-search-group">
                  <label className="owner-select-label" htmlFor="cama-search">Search by Property ID:</label>
                  <div className="cama-search-row">
                    <input
                      id="cama-search"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={camaSearchTerm}
                      onChange={(e) => setCamaSearchTerm(e.target.value.replace(/\D/g, ''))}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleCamaSearch();
                        }
                      }}
                      placeholder="Enter CAMA ID..."
                      className="owner-select owner-search-input cama-search-input"
                    />
                    <button
                      type="button"
                      className="owner-export-button"
                      onClick={handleCamaSearch}
                      disabled={camaLoading || !camaSearchTerm || !countyId}
                    >
                      {camaLoading ? 'Searching...' : 'Search'}
                    </button>
                    {(camaSearchActive || camaSearchTerm) && (
                      <button
                        type="button"
                        className="owner-export-button owner-export-button--secondary"
                        onClick={handleClearCamaSearch}
                        disabled={camaLoading}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {camaError && !camaSearchActive && (
                    <p className="owner-inline-error">{camaError}</p>
                  )}
                </div>
              </div>

              <div className="owner-control owner-control--export">
                <button
                  type="button"
                  className="owner-export-button"
                  onClick={handleExportCsv}
                  disabled={sortedData.length === 0}
                >
                  Export CSV
                </button>
                {camaSearchActive && !camaError && (
                  <div className="owner-total-box" aria-live="polite">
                    <span className="owner-total-label">Total Documents</span>
                    <span className="owner-total-count">
                      {camaLoading ? '...' : (Number.isFinite(camaTotalCount) ? camaTotalCount : '-')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {(!isAdmin || countyId) && (
          camaSearchActive ? (
            camaLoading ? (
              <p style={{ marginTop: 20 }}>Loading CAMA results...</p>
            ) : (
              <>
                {camaError && (
                  <p className="owner-inline-error owner-inline-error--block">{camaError}</p>
                )}
                {camaResults.length === 0 ? (
                  !camaError && (
                    <p className="owner-no-records">
                      {lastCamaIdSearched
                        ? `No attachments found for CAMA ID ${lastCamaIdSearched}.`
                        : 'No attachments found for that CAMA ID.'}
                    </p>
                  )
                ) : (
                  <div className="owner-table-wrapper">
                    <table className="owner-table">
                      <thead>
                        <tr>
                          <th>Owner Name</th>
                          <th>Description</th>
                          <th>Legal Description</th>
                          <th>Situs</th>
                          <th className="text-center">View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {camaResults.map((record, index) => (
                          <tr key={record.FileID || index}>
                            <td>{record.OrganizationName || '-'}</td>
                             <td>{toDisplayUpper(record.Description)}</td>
                            <td>{record.LegalDescription || '-'}</td>
                            <td>{record.ParcelSitus || '-'}</td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="view-button"
                                onClick={() => handleViewCamaFile(record)}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )
          ) : loading ? (
            <p style={{ marginTop: 20 }}>Loading data...</p>
          ) : filtered.length === 0 ? (
            <p className="owner-no-records">
              No records found{year ? ` for ${year}` : ''}{debouncedSearchTerm && ` matching "${debouncedSearchTerm}"`}.
            </p>
          ) : (
            <div className="owner-table-wrapper">
              <table className="owner-table">
                <thead>
                  <tr>
                    {renderSortableHeader('OwnerName', 'Owner Name', { align: 'left' })}
                    <th>CAD Owner ID</th>
                    <th>Agent Name</th>
                    {renderSortableHeader('CurrentValue', 'Current Value',{ align: 'left' })}
                    {renderSortableHeader('PriorValue', 'Prior Value',{ align: 'left' })}
                    <th className="text-center">File Count</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((row, index) => {
                    const isActive = activeOwnerNbr === row.OwnerNbr;
                    return (
                      <tr key={index} className={`owner-row${isActive ? ' owner-row--active' : ''}`}>
                        <td>{row.OwnerName}</td>
                        <td>{row.CAD_OwnerID}</td>
                        <td>{row.AgentName}</td>
                        <td className="numeric-cell">
                          {Number.isFinite(Number(row.CurrentValue))
                            ? `$${Number(row.CurrentValue).toLocaleString()}`
                            : '-'}
                        </td>
                        <td className="numeric-cell">
                          {Number.isFinite(Number(row.PriorValue))
                            ? `$${Number(row.PriorValue).toLocaleString()}`
                            : '-'}
                        </td>
                        <td
                          className={`file-count-cell${isActive ? ' active' : ''}`}
                          onClick={() => handleViewAttachments(row.OwnerNbr, row.OwnerName)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleViewAttachments(row.OwnerNbr, row.OwnerName);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isActive}
                          style={{ cursor: attachmentsLoading ? 'wait' : 'pointer' }}
                        >
                          {row.FileCount}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Attachments modal (unchanged) */}
      {showModal && (
        <div className="attachment-modal-overlay">
          <div className="attachment-modal">
            <div className="modal-heading">
              <h3 className="modal-title">Attachments</h3>
              {selectedOwnerName && (
                <p className="modal-owner-name">{selectedOwnerName}</p>
              )}
            </div>
            <button className="close-button" onClick={() => { setShowModal(false); setSelectedOwnerName(''); }}>&times;</button>

            {attachmentsLoading ? (
              <div className="spinner-container">
                <div className="spinner"></div>
                <p>Loading attachments...</p>
              </div>
            ) : attachments.length === 0 ? (
              <p className="owner-no-records">No attachments found.</p>
            ) : (
              <table className="owner-table">
                <thead>
                  <tr>
                    <th> Uploaded Date</th>
                    <th>Type</th>
                    <th>DESCRIPTION</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {attachments.map((att, idx) => (
                    <tr key={idx}>
                      <td>{formatUploadDate(att.UploadDate)}</td>
                      <td>{att.NAME}</td>
                      <td>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {toDisplayUpper(att.Description)}
                          {isAdmin && Boolean(att.IsAdminOnly) && (
                            <em style={{ color: '#e11d48', fontSize: 14, fontStyle: 'italic', fontWeight: 700 }}>Int</em>
                          )}
                        </span>
                      </td>
                      <td>
                        <button
                          className="view-button"
                          onClick={() =>
                            window.open(
                              buildApiUrl(
                                `owners/owner-attachments/view?ownerNbr=${selectedOwnerNbr}&appraisalYear=${year}&fileId=${att.FileID}` +
                                (att.NAME ? `&name=${encodeURIComponent(att.NAME)}` : '')
                              ),
                              '_blank'
                            )
                          }
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerDetailPage;
